export type Granularity = "1m" | "5m" | "15m" | "1h" | "6h" | "1d";
export type MetricsEnv = "dev" | "qa" | "qa4" | "stage" | "prod";

export const GRANULARITIES: readonly Granularity[] = [
  "1m",
  "5m",
  "15m",
  "1h",
  "6h",
  "1d",
] as const;

export interface TimeRange {
  from: string;
  to: string;
  granularity: Granularity;
  env?: MetricsEnv;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

const MAX_RANGE_DAYS = 90;
const MAX_BUCKETS = 1000;

const GRANULARITY_MS: Record<Granularity, number> = {
  "1m": 1 * MS_PER_MINUTE,
  "5m": 5 * MS_PER_MINUTE,
  "15m": 15 * MS_PER_MINUTE,
  "1h": 60 * MS_PER_MINUTE,
  "6h": 6 * 60 * MS_PER_MINUTE,
  "1d": MS_PER_DAY,
};

export function defaultRange(now: Date = new Date()): TimeRange {
  const to = now.toISOString();
  const from = new Date(now.getTime() - MS_PER_DAY).toISOString();
  return { from, to, granularity: "1h" };
}

export function rangeSpanMs(range: TimeRange): number {
  return new Date(range.to).getTime() - new Date(range.from).getTime();
}

export function bucketCount(range: TimeRange): number {
  const span = rangeSpanMs(range);
  if (span <= 0) return 0;
  return Math.ceil(span / GRANULARITY_MS[range.granularity]);
}

export type RangeValidation =
  | { ok: true }
  | { ok: false; reason: string; suggestedGranularity?: Granularity };

export function validateRange(range: TimeRange): RangeValidation {
  const fromMs = new Date(range.from).getTime();
  const toMs = new Date(range.to).getTime();

  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    return { ok: false, reason: "`from` and `to` must be valid ISO 8601 datetimes" };
  }
  if (toMs <= fromMs) {
    return { ok: false, reason: "`to` must be after `from`" };
  }
  if (toMs - fromMs > MAX_RANGE_DAYS * MS_PER_DAY) {
    return { ok: false, reason: `Range exceeds the ${MAX_RANGE_DAYS}-day server cap` };
  }

  const buckets = bucketCount(range);
  if (buckets > MAX_BUCKETS) {
    const suggested = pickCoarserGranularity(toMs - fromMs);
    return {
      ok: false,
      reason: `Bucket count ${buckets} exceeds the ${MAX_BUCKETS} server cap; pick a coarser granularity`,
      suggestedGranularity: suggested,
    };
  }

  return { ok: true };
}

function pickCoarserGranularity(spanMs: number): Granularity | undefined {
  for (const g of GRANULARITIES) {
    if (Math.ceil(spanMs / GRANULARITY_MS[g]) <= MAX_BUCKETS) return g;
  }
  return undefined;
}

export function toSearchParams(range: TimeRange): URLSearchParams {
  const params = new URLSearchParams();
  params.set("from", range.from);
  params.set("to", range.to);
  params.set("granularity", range.granularity);
  if (range.env) params.set("env", range.env);
  return params;
}
